import { ComponentView } from '../../../types/types'
import type { WidgetType } from '../panels/WidgetPanel'

type EnvironmentValue = 'localhost' | 'staging'
type StagingValue = string
export type ApiBaseUrlValue = string

declare global {
  interface Window {
    RTR_API_URL_OVERWRITTEN: ApiBaseUrlValue
    RTR_API_BASIC_AUTH_USER: string
    RTR_API_BASIC_AUTH_PASS: string
  }
}

// Debug Config types
export interface DebugConfigCredentials {
  basicAuthUser: string
  basicAuthPass: string
}

export interface EnvironmentOption {
  value: string
  label: string
}

export interface StagingOption {
  value: string
  label: string
}

export interface DebugConfig {
  credentials: DebugConfigCredentials
  environments: EnvironmentOption[]
  stagingOptions: StagingOption[]
  urlPattern: string
  baseUrls: {
    localhost: string
    staging: string
  }
}

export interface ConfigError {
  message: string
  canRetry: boolean
}

export interface SelectedAccount {
  id: string
  name: string
  widgetAccessToken: string
}

export interface SelectedItem {
  id: string
  reference: string
  name: string
}

export type ToolbarPosition = 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right'

export interface ToolbarSettings {
  keepOpenOnClickOutside: boolean
  position: ToolbarPosition
}

export type DebugSettings = {
  environment: EnvironmentValue
  staging: StagingValue
  apiBaseUrl: ApiBaseUrlValue
  publishedJs: boolean
}

export interface TrackingState {
  clickId?: string
  referrer?: string
  customParam?: string
}

export const storage = {
  initializeWindowVariables(credentials?: DebugConfigCredentials) {
    window.RTR_API_URL_OVERWRITTEN = this.getDebugSettings().apiBaseUrl
    window.RTR_API_BASIC_AUTH_USER = credentials?.basicAuthUser || ''
    window.RTR_API_BASIC_AUTH_PASS = credentials?.basicAuthPass || ''
  },

  getDebugSettings(): DebugSettings {
    const stored = localStorage.getItem('rentware-debug-settings')
    return stored
      ? JSON.parse(stored)
      : {
          environment: 'localhost',
          staging: 'staging',
          apiBaseUrl: 'http://127.0.0.1:8000',
          publishedJs: false
        }
  },

  setDebugSettings(settings: DebugSettings) {
    window.RTR_API_URL_OVERWRITTEN = settings.apiBaseUrl
    localStorage.setItem('rentware-debug-settings', JSON.stringify(settings))
    // Dispatch custom event to notify components
    window.dispatchEvent(new CustomEvent('debug-settings-changed', { detail: settings }))
  },

  getFavoritesOnly(): boolean {
    const stored = localStorage.getItem('rentware-debug-favorites-only')
    return stored === 'true'
  },

  setFavoritesOnly(favoritesOnly: boolean) {
    localStorage.setItem('rentware-debug-favorites-only', String(favoritesOnly))
  },

  getSelectedAccount(): SelectedAccount | null {
    const stored = localStorage.getItem('rentware-debug-selected-account')
    return stored ? JSON.parse(stored) : null
  },

  setSelectedAccount(accountData: SelectedAccount | null) {
    if (accountData) {
      localStorage.setItem('rentware-debug-selected-account', JSON.stringify(accountData))
    } else {
      localStorage.removeItem('rentware-debug-selected-account')
    }
    // Dispatch custom event to notify listeners in the same window
    window.dispatchEvent(
      new CustomEvent('debug-selected-account-changed', {
        detail: accountData
      })
    )
  },

  getSelectedItem(): SelectedItem | null {
    const stored = localStorage.getItem('rentware-debug-selected-item')
    return stored ? JSON.parse(stored) : null
  },

  setSelectedItem(itemData: SelectedItem | null) {
    if (itemData) {
      localStorage.setItem('rentware-debug-selected-item', JSON.stringify(itemData))
    } else {
      localStorage.removeItem('rentware-debug-selected-item')
    }
    // Dispatch custom event to notify listeners in the same window
    window.dispatchEvent(
      new CustomEvent('debug-selected-item-changed', {
        detail: itemData
      })
    )
  },

  getWidgetView(): ComponentView {
    const stored = localStorage.getItem('rentware-debug-widget-view')
    return stored ? JSON.parse(stored) : ComponentView.Default
  },

  setWidgetView(view: ComponentView) {
    if (view) {
      localStorage.setItem('rentware-debug-widget-view', JSON.stringify(view))
    } else {
      localStorage.removeItem('rentware-debug-widget-view')
    }
  },

  getWidgetType(): WidgetType {
    const stored = localStorage.getItem('rentware-debug-widget-type')
    return stored ? JSON.parse(stored) : 'rtr-article-booking'
  },

  setWidgetType(type: WidgetType) {
    if (type) {
      localStorage.setItem('rentware-debug-widget-type', JSON.stringify(type))
    } else {
      localStorage.removeItem('rentware-debug-widget-type')
    }
  },

  getToolbarVisible(): boolean {
    const isVisible = localStorage.getItem('rentware-debug-toolbar-visible')
    return isVisible !== 'false'
  },

  setToolbarVisible(visible: boolean) {
    localStorage.setItem('rentware-debug-toolbar-visible', String(visible))
  },

  getToolbarSettings(): ToolbarSettings {
    const stored = localStorage.getItem('rentware-debug-toolbar-settings')
    return stored ? JSON.parse(stored) : { keepOpenOnClickOutside: false, position: 'bottom-center' }
  },

  setToolbarSettings(settings: ToolbarSettings) {
    localStorage.setItem('rentware-debug-toolbar-settings', JSON.stringify(settings))
  },

  getFavoriteAccounts(): string[] {
    const stored = localStorage.getItem('rentware-debug-favorite-accounts')
    return stored ? JSON.parse(stored) : []
  },

  setFavoriteAccounts(accountIds: string[]) {
    localStorage.setItem('rentware-debug-favorite-accounts', JSON.stringify(accountIds))
  },

  toggleFavoriteAccount(accountId: string) {
    const favorites = this.getFavoriteAccounts()
    const index = favorites.indexOf(accountId)
    if (index > -1) {
      favorites.splice(index, 1)
    } else {
      favorites.push(accountId)
    }
    this.setFavoriteAccounts(favorites)
    return favorites
  },

  getTrackingState(): TrackingState {
    const stored = localStorage.getItem('rentware-debug-tracking-state')
    return stored ? JSON.parse(stored) : {}
  },

  setTrackingState(state: TrackingState) {
    localStorage.setItem('rentware-debug-tracking-state', JSON.stringify(state))
  },

  clearAll() {
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith('rentware-debug-')) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach(key => {
      localStorage.removeItem(key)
    })
    localStorage.removeItem('accountsData')
  }
}
